import { z } from "zod";

type ValidatorHandler = (schema: z.ZodString, label: string) => z.ZodString;

const SIMPLE_VALIDATORS: Record<string, ValidatorHandler> = {
    required: (schema, label) =>
        schema.min(1, `${label} is required`),

    nospace: (schema, label) =>
        schema.regex(/^\S+$/, `${label} must not contain spaces`),

    email: (schema, label) =>
        schema.email(`${label} must be a valid email`),

    numeric: (schema, label) =>
        schema.regex(/^\d+$/, `${label} must be a number`),

    alpha: (schema, label) =>
        schema.regex(/^[a-zA-Z]+$/, `${label} must contain only letters`),

    alphanumeric: (schema, label) =>
        schema.regex(
            /^[a-zA-Z0-9]+$/,
            `${label} must contain only letters and numbers`,
        ),

    alphanumeric_underscore: (schema, label) =>
        schema.regex(
            /^\w+$/,
            `${label} must contain only letters, numbers, and underscores`,
        ),

    url: (schema, label) =>
        schema.url(`${label} must be a valid URL`),
};

const applyParameterizedValidator = (
    validatorName: string,
    schema: z.ZodString,
    label: string,
): z.ZodString | null => {
    if (validatorName.startsWith("minlength:")) {
        const min = Number(validatorName.split(":")[1]);
        return Number.isFinite(min)
            ? schema.min(min, `${label} must be at least ${min} characters`)
            : schema;
    }

    if (validatorName.startsWith("maxlength:")) {
        const max = Number(validatorName.split(":")[1]);
        return Number.isFinite(max)
            ? schema.max(max, `${label} must be at most ${max} characters`)
            : schema;
    }

    if (validatorName.startsWith("pattern:")) {
        const patternSource = validatorName.slice(validatorName.indexOf(":") + 1);
        try {
            return schema.regex(
                new RegExp(patternSource),
                `${label} format is invalid`,
            );
        } catch {
            return schema;
        }
    }

    return null;
};

export const applyValidator = (
    validatorName: string,
    schema: z.ZodString,
    label: string,
): z.ZodString => {
    const simpleHandler = SIMPLE_VALIDATORS[validatorName];
    if (simpleHandler) return simpleHandler(schema, label);

    return applyParameterizedValidator(validatorName, schema, label) ?? schema;
};